/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { getDefaultStore } from 'jotai';
import { activeSanctioningBodyIdAtom } from 'jotai/sanctioningBody';
import { activeSessionReplayEnabledSelector } from 'jotai/session';
import { useAtomCallback } from 'jotai/utils';
import { visualizationSettingsAtomFamily } from 'jotai/visualizationSettings';
import { AlertData, AlertRequest, SanctioningBodyEnum } from 'models/viewModels';
import { useCallback } from 'react';
import { alertChannelSettingsService } from 'services/alert/AlertChannelSettingsService';
import { alertService } from 'services/alert/AlertService';
import {
  alertNotificationAtomFamily,
  AlertNotificationLogSettingsObject,
  alertNotificationReplayAtomFamily,
  alertToastSettingsIdAtom,
} from '.';

const defaultStore = getDefaultStore();
let backfill: AlertData[] = [];

const setAlertReplay = async (settingsId: string, filter: AlertRequest, replayLap: number) => {
  const replayData = await defaultStore.get(alertNotificationReplayAtomFamily(filter));
  // Only want replayData on or before the replayLap
  const currentAlerts = replayData?.filter((data) => data.lap <= replayLap);
  defaultStore.set(alertNotificationAtomFamily(settingsId), currentAlerts);
};

// Sets the alert backfill for the given filter. Returns the backfill.
const setAlertBackfill = async (settingsId: string, filter: AlertRequest) => {
  backfill = await alertService.getAlertBackfill(filter);
  defaultStore.set(alertNotificationAtomFamily(settingsId), backfill);
  return backfill;
};

// Trigger when a new visualization is initialized or filters change
export const useUpdateAlertFilters = (settingsId: string, filter: AlertRequest, replayLap: number) => {
  return useAtomCallback(
    useCallback(
      async (_get, _set) => {
        const activeSanctioningBodyId = defaultStore.get(activeSanctioningBodyIdAtom);
        const sanctioningBodyEnum = activeSanctioningBodyId ?? SanctioningBodyEnum.NASCAR;
        const isReplay = defaultStore.get(activeSessionReplayEnabledSelector);
        if (isReplay) {
          // set the replay data for the filter
          await setAlertReplay(settingsId, filter, replayLap);
        } else {
          // Check if the channel service has been initialized for the given settingsId and sanctioningBody
          const serviceExists = alertChannelSettingsService.getAlertDataServiceBySettingsIdSanctioningBody(
            settingsId,
            sanctioningBodyEnum
          );
          if (!serviceExists) {
            // Initialize the service and set the initial filter
            alertChannelSettingsService.initializeChannelBySettingsId(settingsId, sanctioningBodyEnum, filter);
            await initializeNewStream(settingsId, sanctioningBodyEnum, filter);
          } else {
            // If the service already exists, check if the filters have changed
            const currentFiltersForSettingsId = alertChannelSettingsService.isFilterActiveBySettingsId(
              settingsId,
              sanctioningBodyEnum,
              filter
            );
            if (!currentFiltersForSettingsId) {
              // Update the stream with the new filters
              updateChannel(settingsId, sanctioningBodyEnum, filter);
            }
          }
        }
      },
      [settingsId, filter, replayLap]
    )
  );
};

// Triggered when a visualization is unmounted (changed tabs or deleted)
export const useUnmountVisualizationBySettingId = (settingsId: string) => {
  return useAtomCallback(
    useCallback(async () => {
      // If the visual has alert toasts turned on, then don't remove the listener or reset the alertToastSettingsId
      // Settings will be undefined if the visualization is deleted
      const settings: AlertNotificationLogSettingsObject = defaultStore.get(
        visualizationSettingsAtomFamily(settingsId)
      );
      const alertToastSettingsId = defaultStore.get(alertToastSettingsIdAtom);
      // Settings will be undefined if the visualization is deleted
      // Reset the alertToastSettingsId if
      // - alertToastSettingsId is the same as the settingsId and the settings are undefined (deleted) or the enableNotifications is false
      if (alertToastSettingsId && alertToastSettingsId === settingsId && !settings) {
        defaultStore.set(alertToastSettingsIdAtom, undefined);
      }
      // If the settings are turned off or the settings are undefined (deleted), remove the listener
      // or if the alertToastSettingsId is not the same as the settingsId or is undefined
      if (
        !settings ||
        (alertToastSettingsId && alertToastSettingsId !== settingsId) ||
        alertToastSettingsId === undefined
      ) {
        // Vis is unmounted, stop the channel for that settingsId
        alertChannelSettingsService.removeAllListeners(settingsId);
        // Clear the atom (just in case that settingId is somehow generated again)
        defaultStore.set(alertNotificationAtomFamily(settingsId), undefined);
      }
    }, [settingsId])
  );
};

// Helper Function to initialize a new stream
const initializeNewStream = async (settingsId: string, sanctioningBody: SanctioningBodyEnum, filter: AlertRequest) => {
  const activeAlertService = alertChannelSettingsService.getAlertDataServiceBySettingsIdSanctioningBody(
    settingsId,
    sanctioningBody
  );
  // Initialize the backfill and set alertNotificationAtomFamily to that backfill
  await setAlertBackfill(settingsId, filter);
  // Start the channel connection by adding a listener
  // This first callback in the addListener will be used even if the filter changes
  activeAlertService?.addListener(
    (data) => {
      // Check if the data is newer than the most recent backfill data (if any)
      // Backfill from stream metrics is in timestamp descending order so the first element is the most recent
      if (backfill.length === 0 || (backfill.length > 0 && data.timestamp > backfill[0]?.timestamp)) {
        // Add the last 1000 messages to the state
        defaultStore.set(alertNotificationAtomFamily(settingsId), (prev) =>
          [data, ...((prev ?? []) as AlertData[])].slice(0, 1000)
        );
      }
    },
    () => {
      defaultStore.set(alertNotificationAtomFamily(settingsId), undefined);
    },
    settingsId
  );
};

const updateChannel = async (settingsId: string, sanctioningBody: SanctioningBodyEnum, filter: AlertRequest) => {
  // Reset the backfill using the new filter
  await setAlertBackfill(settingsId, filter);
  // Call the channel service to update the request
  alertChannelSettingsService.updateRequestBySettingsId(settingsId, sanctioningBody, filter);
};
